import * as React from "react";
import {
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { Table } from "@/components/ui/table";
import OptimizationButton from "./OptimizationButton";
import { Product } from "@/types/Products";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { useEffect } from "react";

import MergeVariantsDropdown from "./product-table/MergeVariantsDropdown";
import ProductFilterDropdown from "./product-table/ProductFilterDropdown";
import ProductSyncButton from "./product-table/ProductSyncButton";
import ProductTableHeader from "./product-table/ProductTableHeader";
import ProductTableBody from "./product-table/ProductTableBody";
import ProductTableFooter from "./product-table/ProductTableFooter";
import { useProductTableColumns } from "@/hooks/products/useProductTableColumns";
import { useProductMerging } from "@/hooks/products/useProductMerging";

export default function ProductTable() {
    const [sorting, setSorting] = React.useState<SortingState>(() => {
        const cached = localStorage.getItem("productTable.sorting");
        return cached ? JSON.parse(cached) : [];
    });
    const [columnFilters, setColumnFilters] =
        React.useState<ColumnFiltersState>(() => {
            const cached = localStorage.getItem("productTable.columnFilters");
            return cached ? JSON.parse(cached) : [];
        });
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>(() => {
            const cached = localStorage.getItem(
                "productTable.columnVisibility"
            );
            return cached
                ? JSON.parse(cached)
                : {
                      is_converged: false,
                      optimization_data: false,
                      category: false,
                      product_type: false,
                      tags: false,
                      sku: false,
                  };
        });
    const [rowSelection, setRowSelection] = React.useState({});
    const [isLoadingProducts, setIsLoadingProducts] = React.useState(true);
    const [isLoadingPage, setIsLoadingPage] = React.useState(false);
    const [products, setProducts] = React.useState<Product[]>([]);
    const [isSyncing, setIsSyncing] = React.useState(false);
    const [shopName, setShopName] = React.useState<string | null>(null);
    const [lastSync, setLastSync] = React.useState<string | null>(null);
    const [markets, setMarkets] = React.useState<string[]>([]);
    const { session, isDemoMode, shop } = useAuth();
    const [priceRange, setPriceRange] = React.useState<[number, number]>(() => {
        const cached = localStorage.getItem("productTable.priceRange");
        return cached ? JSON.parse(cached) : [0, 1000];
    });
    const [maxPrice, setMaxPrice] = React.useState(1000);
    const [overallMaxPrice, setOverallMaxPrice] = React.useState(1000);
    const [totalCount, setTotalCount] = React.useState(0);
    const prevFiltersRef = React.useRef(columnFilters);
    const [pageSelections, setPageSelections] = React.useState<
        Record<number, Record<string, boolean>>
    >({});
    const [selectedProducts, setSelectedProducts] = React.useState<Product[]>(
        []
    );

    useEffect(() => {
        localStorage.setItem("productTable.sorting", JSON.stringify(sorting));
    }, [sorting]);

    useEffect(() => {
        localStorage.setItem(
            "productTable.columnFilters",
            JSON.stringify(columnFilters)
        );
    }, [columnFilters]);

    useEffect(() => {
        localStorage.setItem(
            "productTable.columnVisibility",
            JSON.stringify(columnVisibility)
        );
    }, [columnVisibility]);

    useEffect(() => {
        localStorage.setItem(
            "productTable.priceRange",
            JSON.stringify(priceRange)
        );
    }, [priceRange]);

    const fetchProducts = async () => {
        setIsLoadingProducts(true);
        try {
            // Get max price first
            const { data: maxPriceData, error: maxPriceError } = await supabase
                .from("products")
                .select("price")
                .eq("user_id", session?.user.id)
                .order("price", { ascending: false })
                .limit(1)
                .single();

            if (maxPriceError) throw maxPriceError;
            if (maxPriceData) {
                setOverallMaxPrice(Math.ceil(maxPriceData.price));
            }

            // Build the base query
            let query = supabase
                .from("products")
                .select("*", { count: "exact" })
                .eq("user_id", session?.user.id);

            // Apply filters
            const productNameFilter = table
                .getColumn("product_name")
                ?.getFilterValue() as string;
            if (productNameFilter) {
                query = query.or(
                    `product_name.ilike.%${productNameFilter}%,variant_name.ilike.%${productNameFilter}%,sku.ilike.%${productNameFilter}%`
                );
            }

            const categoryFilter = table
                .getColumn("category")
                ?.getFilterValue() as string;
            if (categoryFilter) {
                query = query.eq("category", categoryFilter);
            }

            const productTypeFilter = table
                .getColumn("product_type")
                ?.getFilterValue() as string;
            if (productTypeFilter) {
                query = query.eq("product_type", productTypeFilter);
            }

            const statusFilter = table
                .getColumn("status")
                ?.getFilterValue() as string;
            if (statusFilter) {
                query = query.eq("status", statusFilter);
            }

            const [minPrice, maxPrice] = priceRange;
            if (minPrice > 0 || maxPrice < 1000) {
                query = query.gte("price", minPrice).lte("price", maxPrice);
            }

            // Apply sorting
            const sorting = table.getState().sorting;
            if (sorting.length > 0) {
                const { id, desc } = sorting[0];
                query = query.order(id, { ascending: !desc });
            } else {
                query = query.order("id", { ascending: true });
            }

            // Apply pagination
            const pageSize = table.getState().pagination.pageSize;
            const pageIndex = table.getState().pagination.pageIndex;
            const from = pageIndex * pageSize;
            const to = from + 1000;
            query = query.range(from, to);

            // Execute query
            const { data, error, count } = await query;

            if (error) throw error;

            // Group merged products
            const groupedProducts = (data ?? []).reduce(
                (acc: Product[], product) => {
                    if (!product.merge_id) {
                        acc.push(product);
                        return acc;
                    }

                    const existingGroup = acc.find(
                        (p) => p.shopify_variant_id === product.merge_id
                    );
                    if (existingGroup) {
                        const productNames = new Set(
                            existingGroup.product_name.split(", ")
                        );
                        productNames.add(product.product_name);
                        existingGroup.product_name =
                            Array.from(productNames).join(", ");

                        const variantNames = new Set(
                            existingGroup.variant_name.split(", ")
                        );
                        variantNames.add(product.variant_name);
                        existingGroup.variant_name =
                            Array.from(variantNames).join(", ");
                        existingGroup.is_merged = true;
                    } else {
                        const baseProduct = data?.find(
                            (p) => p.shopify_variant_id === product.merge_id
                        );
                        if (baseProduct) {
                            const productNames = new Set([
                                baseProduct.product_name,
                                product.product_name,
                            ]);

                            const variantNames = new Set([
                                baseProduct.variant_name,
                                product.variant_name,
                            ]);

                            acc.push({
                                ...baseProduct,
                                product_name:
                                    Array.from(productNames).join(", "),
                                variant_name:
                                    Array.from(variantNames).join(", "),
                                is_merged: true,
                            });
                        }
                    }
                    return acc;
                },
                []
            );

            setProducts(groupedProducts.slice(0, pageSize));
            setTotalCount(count || 0);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setIsLoadingProducts(false);
            setIsLoadingPage(false);
        }
    };

    const fetchShopName = async () => {
        const { data } = await supabase
            .from("shops")
            .select("shop, last_product_sync, markets")
            .single();

        if (data) {
            setShopName(data.shop);
            setLastSync(data.last_product_sync);
            setMarkets(data.markets);
        }
    };

    const syncProducts = async () => {
        if (isSyncing) return;

        setIsSyncing(true);
        try {
            const response = await fetch(
                `${
                    import.meta.env.VITE_OPTIFI_API_URL
                }/integrations/shopify/product-sync`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ shop: shopName }),
                }
            );

            if (!response.ok) {
                throw new Error("Sync failed");
            }

            const newSyncTime = new Date().toISOString();

            await supabase
                .from("shops")
                .update({ last_product_sync: newSyncTime })
                .eq("shop", shopName);

            setLastSync(newSyncTime);
            await fetchProducts();
        } catch (error) {
            console.error("Error syncing products:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchShopName();
    }, [session]);

    useEffect(() => {
        if (!lastSync) return;

        if (isDemoMode) return;

        const lastSyncDate = new Date(lastSync);
        const secondsSinceLastSync =
            (Date.now() - lastSyncDate.getTime()) / 1000;

        if (secondsSinceLastSync > 3600) {
            syncProducts();
        }
    }, [lastSync]);

    const handleSelectAll = async () => {
        // Build the base query with all current filters
        let query = supabase
            .from("products")
            .select("*")
            .eq("user_id", session?.user.id);

        // Apply filters
        const productNameFilter = table
            .getColumn("product_name")
            ?.getFilterValue() as string;
        if (productNameFilter) {
            query = query.or(
                `product_name.ilike.%${productNameFilter}%,variant_name.ilike.%${productNameFilter}%,sku.ilike.%${productNameFilter}%`
            );
        }

        const categoryFilter = table
            .getColumn("category")
            ?.getFilterValue() as string;
        if (categoryFilter) {
            query = query.eq("category", categoryFilter);
        }

        const productTypeFilter = table
            .getColumn("product_type")
            ?.getFilterValue() as string;
        if (productTypeFilter) {
            query = query.eq("product_type", productTypeFilter);
        }

        const statusFilter = table
            .getColumn("status")
            ?.getFilterValue() as string;
        if (statusFilter) {
            query = query.eq("status", statusFilter);
        }

        const [minPrice, maxPrice] = priceRange;
        if (minPrice > 0 || maxPrice < 1000) {
            query = query.gte("price", minPrice).lte("price", maxPrice);
        }

        // Apply sorting
        const sorting = table.getState().sorting;
        if (sorting.length > 0) {
            const { id, desc } = sorting[0];
            query = query.order(id, { ascending: !desc });
        } else {
            query = query.order("id", { ascending: true });
        }

        // Limit to 1000 products
        query = query.limit(1000);

        const { data: allProducts, error } = await query;

        if (error) {
            console.error("Error fetching filtered products:", error);
            return;
        }

        if (allProducts) {
            // Group merged products
            const groupedProducts = allProducts.reduce(
                (acc: Product[], product) => {
                    if (!product.merge_id) {
                        acc.push(product);
                        return acc;
                    }

                    const existingGroup = acc.find(
                        (p) => p.shopify_variant_id === product.merge_id
                    );
                    if (existingGroup) {
                        const productNames = new Set(
                            existingGroup.product_name.split(", ")
                        );
                        productNames.add(product.product_name);
                        existingGroup.product_name =
                            Array.from(productNames).join(", ");

                        const variantNames = new Set(
                            existingGroup.variant_name.split(", ")
                        );
                        variantNames.add(product.variant_name);
                        existingGroup.variant_name =
                            Array.from(variantNames).join(", ");
                        existingGroup.is_merged = true;
                    } else {
                        const baseProduct = allProducts.find(
                            (p) => p.shopify_variant_id === product.merge_id
                        );
                        if (baseProduct) {
                            const productNames = new Set([
                                baseProduct.product_name,
                                product.product_name,
                            ]);

                            const variantNames = new Set([
                                baseProduct.variant_name,
                                product.variant_name,
                            ]);

                            acc.push({
                                ...baseProduct,
                                product_name:
                                    Array.from(productNames).join(", "),
                                variant_name:
                                    Array.from(variantNames).join(", "),
                                is_merged: true,
                            });
                        }
                    }
                    return acc;
                },
                []
            );

            setSelectedProducts(groupedProducts);
            // Select all rows on current page
            table.toggleAllPageRowsSelected(true);
        }
    };

    const columns = useProductTableColumns({
        selectedProducts,
        totalCount,
        products,
        setSelectedProducts,
        setPageSelections,
        handleSelectAll,
        fetchProducts,
        markets,
    });

    const table = useReactTable<Product>({
        data: products,
        columns,
        enableSorting: true,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        initialState: {
            pagination: {
                pageSize: 8,
            },
            columnVisibility: {
                is_converged: false,
                optimization_data: false,
                category: false,
                sku: false,
            },
        },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / 8), // Use fixed page size for initial calculation
        autoResetPageIndex: false,
    });

    // Add effect to refetch when pagination, sorting, or filters change
    useEffect(() => {
        const currentFilters = table.getState().columnFilters;
        const filtersChanged =
            JSON.stringify(prevFiltersRef.current) !==
            JSON.stringify(currentFilters);

        if (filtersChanged) {
            table.setPageIndex(0);
            prevFiltersRef.current = currentFilters;
            // Clear all selections when filters change
            setSelectedProducts([]);
            setPageSelections({});
            table.toggleAllRowsSelected(false);
        }

        setIsLoadingPage(true);
        fetchProducts();
    }, [
        table.getState().pagination.pageIndex,
        table.getState().pagination.pageSize,
        table.getState().sorting,
        table.getState().columnFilters,
        priceRange,
    ]);

    // Add separate effect for price range changes
    useEffect(() => {
        table.setPageIndex(0);
        setIsLoadingPage(true);
        fetchProducts();
    }, [priceRange]);

    // Add effect to handle page changes
    useEffect(() => {
        const currentPage = table.getState().pagination.pageIndex;
        setRowSelection(pageSelections[currentPage] || {});
    }, [table.getState().pagination.pageIndex]);

    // Add effect to save selection state when it changes
    useEffect(() => {
        const currentPage = table.getState().pagination.pageIndex;
        setPageSelections((prev) => ({
            ...prev,
            [currentPage]: rowSelection,
        }));
    }, [rowSelection]);

    const { mergeProducts, unmergeProducts, autoMergeVariants } =
        useProductMerging(selectedProducts, () => {
            fetchProducts();
        });

    return (
        <>
            <div className="flex items-center gap-4 py-4">
                <OptimizationButton
                    variant="start"
                    table={table}
                    selectedProducts={selectedProducts}
                    onClose={() => {
                        fetchProducts();
                    }}
                />
                <OptimizationButton
                    variant="stop"
                    table={table}
                    selectedProducts={selectedProducts}
                    onClose={() => {
                        fetchProducts();
                    }}
                />

                <MergeVariantsDropdown
                    selectedProducts={selectedProducts}
                    mergeProducts={mergeProducts}
                    unmergeProducts={unmergeProducts}
                    autoMergeVariants={autoMergeVariants}
                />
                <ProductFilterDropdown
                    table={table}
                    columnFilters={columnFilters}
                    setPriceRange={setPriceRange}
                    priceRange={priceRange}
                    maxPrice={maxPrice}
                    overallMaxPrice={overallMaxPrice}
                    shop={shop}
                />
                <ProductSyncButton
                    isSyncing={isSyncing}
                    syncProducts={syncProducts}
                />
            </div>
            <div className="rounded-md border">
                <Table className="table-auto relative">
                    <ProductTableHeader
                        table={table}
                        selectedProducts={selectedProducts}
                        totalCount={totalCount}
                        products={products}
                        setSelectedProducts={setSelectedProducts}
                        setPageSelections={setPageSelections}
                        handleSelectAll={handleSelectAll}
                    />
                    <ProductTableBody
                        table={table}
                        isLoadingPage={isLoadingPage}
                        columns={columns}
                        isLoadingProducts={isLoadingProducts}
                    />
                </Table>
            </div>
            <div className="flex items-center justify-between py-4">
                <ProductTableFooter
                    table={table}
                    selectedProducts={selectedProducts}
                    totalCount={totalCount}
                    lastSync={lastSync}
                />
            </div>
        </>
    );
}
